import React, { useState, useEffect } from 'react';
import { 
  Link, 
  ExternalLink, 
  CheckCircle, 
  AlertCircle, 
  Settings, 
  Plus,
  Trash2,
  Edit3,
  Globe,
  Mail,
  Calendar,
  Phone,
  DollarSign,
  Database,
  Zap
} from 'lucide-react';
import api from '../utils/api';
import IntegrationSetupWizard from './IntegrationSetupWizard';
import WebhookApiWizard from './WebhookApiWizard';


const CompanyIntegrationsSettings = ({ user, onShowImportWizard }) => {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  const [showWebhookWizard, setShowWebhookWizard] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [webhookStatuses, setWebhookStatuses] = useState({});

  const availableIntegrations = [
    {
      id: 'highlevel',
      name: 'GoHighLevel',
      description: 'All-in-one CRM with pipeline management, marketing automation, SMS, and email campaigns',
      icon: '🎯',
      category: 'CRM & Marketing',
      status: 'available',
      isLaunchCritical: true,
      hasSetupWizard: true,
      isImplemented: true
    },
    {
      id: 'custom-webhooks',
      name: 'Custom Webhooks',
      description: 'Build custom integrations with webhook endpoints',
      icon: '🔗',
      category: 'Development',
      status: 'available',
      hasSetupWizard: true,
      isImplemented: true
    }
  ];

  useEffect(() => {
    fetchIntegrations();
    checkForSuccessMessage();
  }, []);

  const checkForSuccessMessage = () => {
    // Check URL parameters for success messages
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const webhooksEnabled = urlParams.get('webhooks');
    
    if (success) {
      let message = '';
      if (success === 'gohighlevel') {
        message = webhooksEnabled === 'enabled' 
          ? '🎉 GoHighLevel connected successfully with real-time SMS import enabled!'
          : '✅ GoHighLevel connected successfully!';
      }
      
      if (message) {
        setSuccessMessage(message);
        // Clear the URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
        // Auto-hide success message after 5 seconds
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    }
  };

  const fetchIntegrations = async () => {
    setLoading(true);
    
    try {
      // Check integration status using unified endpoint
      const response = await api.get(`/integrations/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      
      const integrationStatuses = [];
      
      // Check GoHighLevel status
      if (response.data.gohighlevel?.connected) {
        integrationStatuses.push({
          id: 'highlevel',
          name: 'GoHighLevel',
          status: 'connected',
          lastSync: response.data.gohighlevel.connected_at || new Date().toISOString(),
          config: {},
          realtimeImport: true // Assume webhooks are set up for connected integrations
        });
      }
      
      // Check Zoom status
      if (response.data.zoom?.connected) {
        integrationStatuses.push({
          id: 'zoom',
          name: 'Zoom',
          status: 'connected',
          lastSync: response.data.zoom.connected_at || new Date().toISOString(),
          config: {}
        });
      }
      
      setIntegrations(integrationStatuses);
    } catch (error) {
      console.error('Error fetching integrations:', error);
      setIntegrations([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = (integrationId) => {
    const integration = availableIntegrations.find(i => i.id === integrationId);
    
    if (!integration?.isImplemented) {
      alert('This integration is coming soon! We\'re working hard to bring it to you.');
      return;
    }
    
    if (integration.hasSetupWizard) {
      if (integrationId === 'custom-webhooks') {
        setShowWebhookWizard(true);
      } else {
        setSelectedIntegration(integration);
        setShowSetupWizard(true);
      }
    } else {
      // Direct connection for integrations without wizards
      initiateConnection(integrationId);
    }
  };

  const initiateConnection = async (integrationId) => {
    setLoading(true);
    try {
      let endpoint = '';
      
      switch (integrationId) {
        case 'highlevel':
          endpoint = '/integrations/gohighlevel/authorize';
          break;
        case 'microsoft-365':
          endpoint = '/microsoft/oauth/authorize';
          break;
        case 'zoom':
          endpoint = '/integrations/zoom/authorize';
          break;
        default:
          throw new Error(`Unknown integration: ${integrationId}`);
      }

      // Get fresh token and make request
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      console.log(`Attempting to connect ${integrationId} via ${endpoint}`);
      
      const response = await api.get(`${endpoint}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 30000
      });

      console.log('OAuth response:', response.data);

      if (response.data.authorization_url) {
        console.log('Redirecting to:', response.data.authorization_url);
        window.location.href = response.data.authorization_url;
      } else {
        throw new Error('No authorization URL received from server');
      }

    } catch (error) {
      console.error('Connection error:', error);
      
      if (error.response?.status === 401) {
        alert('Authentication expired. Please refresh the page and log in again.');
      } else if (error.response?.status === 503) {
        alert(`${integrationId} integration is not configured on the server. Please contact support.`);
      } else {
        alert(`Failed to start connection process: ${error.message}. Please try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async (integrationId) => {
    if (!window.confirm('Are you sure you want to disconnect this integration? This will remove all sync settings and stop data synchronization.')) return;

    try {
      setLoading(true);
      
      let endpoint = '';
      switch (integrationId) {
        case 'highlevel':
          endpoint = '/integrations/ghl/disconnect';
          break;
        case 'microsoft-365':
          endpoint = '/microsoft/disconnect';
          break;
        case 'zoom':
          endpoint = '/integrations/zoom/disconnect';
          break;
        default:
          throw new Error(`Unknown integration: ${integrationId}`);
      }

      const response = await api.post(`${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });

      if (response.data.success !== false) {
        // Remove from local state
        setIntegrations(prev => prev.filter(i => i.id !== integrationId));
        alert('Integration disconnected successfully!');
      } else {
        throw new Error(response.data.message || 'Disconnect failed');
      }
    } catch (error) {
      console.error('Error disconnecting integration:', error);
      if (error.response?.status === 404) {
        // Integration was not connected anyway, just remove from UI
        setIntegrations(prev => prev.filter(i => i.id !== integrationId));
        alert('Integration was not connected - removed from display.');
      } else {
        alert(`Error disconnecting integration: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'available':
        return <AlertCircle className="w-4 h-4 text-blue-500" />;
      case 'coming_soon':
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusText = (status, isImplemented = true) => {
    switch (status) {
      case 'connected':
        return 'Connected';
      case 'available':
        return isImplemented ? 'Ready to Connect' : 'Coming Soon';
      case 'coming_soon':
        return 'Coming Soon';
      default:
        return 'Unknown';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'CRM & Marketing':
        return <DollarSign className="w-5 h-5 text-green-600" />;
      case 'Productivity':
        return <Mail className="w-5 h-5 text-blue-600" />;
      case 'Communication':
        return <Phone className="w-5 h-5 text-purple-600" />;
      case 'Automation':
        return <Zap className="w-5 h-5 text-yellow-600" />;
      case 'Development':
        return <Globe className="w-5 h-5 text-gray-600" />;
      case 'Finance':
        return <DollarSign className="w-5 h-5 text-emerald-600" />;
      default:
        return <Settings className="w-5 h-5 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const connectedIntegrations = availableIntegrations.filter(ai => 
    integrations.some(ci => ci.id === ai.id)
  );

  const availableToConnect = availableIntegrations.filter(ai => 
    !integrations.some(ci => ci.id === ai.id)
  );

  return (
    <div className="p-6 max-w-6xl">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Company Integrations</h2>
            <p className="text-gray-600">Manage company-level integrations that affect your entire organization.</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onShowImportWizard}
              className="inline-flex items-center px-4 py-2 border border-purple-200 text-sm font-medium rounded-md shadow-sm text-purple-700 bg-purple-50 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <Database className="w-4 h-4 mr-2" />
              Import Data
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Integration
            </button>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
            <span className="text-green-700 font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Connected Integrations */}
      {connectedIntegrations.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Connected Integrations</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {connectedIntegrations.map((integration) => {
              const connectedData = integrations.find(ci => ci.id === integration.id);
              return (
                <div key={integration.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className="text-2xl">{integration.icon}</div>
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900">{integration.name}</h4>
                        <p className="text-xs text-gray-500 mt-1">{integration.description}</p>
                        <div className="flex items-center mt-2">
                          {getStatusIcon('connected')}
                          <span className="text-xs text-green-600 ml-1">Connected</span>
                        </div>
                        
                        {/* Real-time Import Status */}
                        <div className="flex items-center mt-2">
                          <Zap className="w-3 h-3 text-blue-500 mr-1" />
                          <span className="text-xs text-blue-600">
                            {connectedData?.realtimeImport ? 'Real-time import active' : 'Manual import only'}
                          </span>
                        </div>
                        
                        {connectedData?.lastSync && (
                          <p className="text-xs text-gray-400 mt-1">
                            Last sync: {new Date(connectedData.lastSync).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => alert('Configure integration (Not implemented)')}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDisconnect(integration.id)}
                        className="p-1 text-red-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Available Integrations */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Integrations</h3>
        
        {/* Category Groups */}
        {['CRM & Marketing', 'Productivity', 'Communication', 'Automation', 'Development', 'Finance'].map(category => {
          const categoryIntegrations = availableToConnect.filter(i => i.category === category);
          if (categoryIntegrations.length === 0) return null;

          return (
            <div key={category} className="mb-6">
              <h4 className="text-md font-medium text-gray-700 mb-3 flex items-center">
                {getCategoryIcon(category)}
                <span className="ml-2">{category}</span>
                {category === 'CRM & Marketing' && (
                  <span className="ml-2 px-2 py-1 text-xs font-semibold text-orange-800 bg-orange-100 rounded-full">
                    LAUNCH CRITICAL
                  </span>
                )}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categoryIntegrations.map((integration) => (
                  <div key={integration.id} className={`bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow ${
                    integration.isLaunchCritical ? 'border-orange-300 bg-orange-50' : 'border-gray-200'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className="text-2xl">{integration.icon}</div>
                        <div className="flex-1">
                          <div className="flex items-center">
                            <h4 className="text-sm font-medium text-gray-900">{integration.name}</h4>
                            {integration.isLaunchCritical && (
                              <span className="ml-2 w-2 h-2 bg-orange-500 rounded-full"></span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{integration.description}</p>
                          <div className="flex items-center mt-2">
                            {getStatusIcon(integration.status)}
                            <span className="text-xs text-gray-600 ml-1">{getStatusText(integration.status, integration.isImplemented)}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleConnect(integration.id)}
                        disabled={!integration.isImplemented}
                        className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${
                          !integration.isImplemented
                            ? 'text-gray-500 bg-gray-100 cursor-not-allowed'
                            : integration.isLaunchCritical 
                              ? 'text-orange-700 bg-orange-100 hover:bg-orange-200 focus:ring-orange-500'
                              : 'text-purple-700 bg-purple-100 hover:bg-purple-200 focus:ring-purple-500'
                        }`}
                      >
                        {!integration.isImplemented ? (
                          <>
                            <Calendar className="w-4 h-4 mr-2" />
                            Coming Soon
                          </>
                        ) : (
                          <>
                            {integration.hasSetupWizard ? (
                              <>
                                <Settings className="w-4 h-4 mr-2" />
                                Setup
                              </>
                            ) : (
                              <>
                                <ExternalLink className="w-4 h-4 mr-2" />
                                Connect
                              </>
                            )}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Integration Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Add Custom Integration</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Plus className="w-6 h-6 rotate-45" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-6">
                Choose how you'd like to extend Athena's capabilities with external integrations.
              </p>
              
              <div className="space-y-4">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setShowWebhookWizard(true);
                  }}
                  className="w-full p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
                >
                  <div className="flex items-center space-x-3">
                    <Globe className="w-8 h-8 text-blue-500" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Webhook & API Setup</h4>
                      <p className="text-sm text-gray-600">Configure custom webhooks and API access for external applications</p>
                    </div>
                  </div>
                </button>
                
                <button
                  disabled
                  className="w-full p-4 border border-gray-200 rounded-lg bg-gray-50 text-left opacity-60 cursor-not-allowed"
                >
                  <div className="flex items-center space-x-3">
                    <Database className="w-8 h-8 text-gray-400" />
                    <div>
                      <h4 className="font-semibold text-gray-600">Custom Integration</h4>
                      <p className="text-sm text-gray-500">Build custom connections (Coming Soon)</p>
                    </div>
                  </div>
                </button>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Integration Setup Wizard */}
      <IntegrationSetupWizard
        isOpen={showSetupWizard}
        onClose={() => {
          setShowSetupWizard(false);
          setSelectedIntegration(null);
        }}
        user={user}
        selectedIntegration={selectedIntegration}
      />

      {/* General Webhook/API Wizard */}
      <WebhookApiWizard
        isOpen={showWebhookWizard}
        onClose={() => setShowWebhookWizard(false)}
        user={user}
      />
    </div>
  );
};

export default CompanyIntegrationsSettings;